import React, { useMemo, useState, useEffect } from 'react';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Anchor,
  Center,
  Group,
  Stack,
  Switch,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import SQLEditorPopup from '../SQLEditorPopup';
import MembershipPopup from '../MembershipPopup';

function PurchasingDepartmentNewVendorRFQDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for pop-up dialogs for query links
  const [openNewVendorSummary, setOpenNewVendorSummary] = useState(false);
  const [openNewVendorPODetails, setOpenNewVendorPODetails] = useState(false);
  const [openMembership, setOpenMembership] = useState(false);

  // Get the period and filter parameters from the query string
  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  // Construct the REST endpoint URL with both period and filter parameters
  const url = `${config.apiBaseUrl}/supply-chain/purchasing/department/scorecard/new-vendor-rfq-details?period=${period}&filter=${warehouseFilter}`;

  const { data, isLoading, error } = useAuthenticatedData(url);

  // When a tab is clicked, update the query param in the URL.
  const handleTabChange = (newPeriod) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('period', newPeriod);
    newSearchParams.set('filter', warehouseFilter);
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  // Process the REST data into rows for the table.
  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index, // Add unique ID for table
      ...item.values, // Unwrap the values object
    }));
  }, [data]);

  // Generate export filename: e.g. Purchasing-MTD-new-vendor-rfq-details
  const generateExportFileName = () => {
    return `Purchasing-${period}-new-vendor-rfq-details`;
  };

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Vendor Name': row.vendorname || '',
      'Buyer': row.buyer || '',
      'Doc No': row.docno || '',
      'User Line': row.userline || '',
      'Added On': row.addedon ? new Date(row.addedon).toLocaleDateString() : '',
      'Part Number': row.partnumber || '',
      'Description': row.description || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  // Define table columns based on the actual API response structure.
  const columns = useMemo(
    () => [
      { accessorKey: 'vendorname', header: 'Vendor Name', size: 200 },
      { accessorKey: 'buyer', header: 'Buyer', size: 150 },
      { accessorKey: 'docno', header: 'Doc No', size: 120 },
      { accessorKey: 'userline', header: 'User Line', size: 80 },
      {
        accessorKey: 'addedon',
        header: 'Added On',
        size: 150,
        Cell: ({ cell }) => {
          if (!cell.getValue()) return '';
          return new Date(cell.getValue()).toLocaleDateString();
        },
      },
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      { accessorKey: 'description', header: 'Description', size: 180 },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header with title and checkbox */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Scorecard - New Vendor RFQ Details
        </Text>
        
        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* About the Data and Query Links Block */}
      <Stack gap="sm" mb="sm">
        {/* Technical options: small links for debugging - Now Above */}
        <Group gap="sm">
          <Anchor
            size="xs"
            c="dimmed"
            onClick={() => setOpenMembership(true)}
            style={{ cursor: 'pointer' }}
          >
            Who is Included?
          </Anchor>
          <Anchor
            size="xs"
            c="dimmed"
            onClick={() =>
              window.open(
                '/query-commander/editor?queryKey=purchasing_dept_new_vendor_growth_summary',
                '_blank'
              )
            }
            style={{ cursor: 'pointer' }}
          >
            Summary Query
          </Anchor>
          <Anchor
            size="xs"
            c="dimmed"
            onClick={() =>
              window.open(
                '/query-commander/editor?queryKey=scorecards-supply-chain-purchasing-department-scorecard-new-vendor-base-growth-rfq-details',
                '_blank'
              )
            }
            style={{ cursor: 'pointer' }}
          >
            Details Query
          </Anchor>
        </Group>

        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Text size="sm" fw={500} mb={4}>
            About the Data
          </Text>
          <Text size="xs" c="dimmed">
            The top-level number is the unique vendors we've sent RFQs to in the selected period that we didn't buy from last year.
            Below is the list of RFQs sent to those vendors - while the top number only counts a vendor once, the list below may have multiple RFQs to the same vendor.
            The RFQs are restricted to the purchasing team, which you can see by clicking the "Who is Included?" link above.
          </Text>
        </Card>
      </Stack>

      <Box mt="sm">
        {/* Tabs for switching periods */}
        <Tabs value={period} onChange={handleTabChange} mb="sm">
          <Tabs.List>
            <Tabs.Tab value="MTD">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="QTD">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="YTD">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      <Paper shadow="sm" p="sm" style={{ height: 400 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>

      
      </Paper>
    </Paper>
  );
}

export default PurchasingDepartmentNewVendorRFQDetails;